// frontend/src/lib/google-places.ts
// Lazy loader for Google Maps Places (new) — the 2026 `PlaceAutocompleteElement`
// widget (the legacy `Autocomplete` class is deprecated). The API key comes from
// VITE_GOOGLE_PLACES_API_KEY, which is operator-provisioned (GCP project with
// "Places API (new)" enabled + billing). When the key is absent, callers fall
// back to manual address entry — `isPlacesConfigured()` gates that.
//
// We hand-roll the minimal surface we use instead of pulling @types/google.maps
// (a very large dependency) for one widget.

const KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined;

interface AddressComponent {
  types: string[];
  longText: string | null;
  shortText: string | null;
}

interface GooglePlace {
  formattedAddress: string | null;
  addressComponents: AddressComponent[] | null;
  fetchFields(opts: { fields: string[] }): Promise<unknown>;
}

interface PlacePrediction {
  toPlace(): GooglePlace;
}

// The `gmp-select` event payload from PlaceAutocompleteElement.
export interface GmpSelectEvent extends Event {
  placePrediction: PlacePrediction;
}

// `importLibrary` as both the bootstrap shim and the consumer use it: it takes
// the library name (plus Google's internal extra args) and resolves that
// library's exports. We only read `PlaceAutocompleteElement`. Optional on the
// namespace below because it is absent until the shim (or the real Maps JS)
// installs it.
type ImportMapsLibrary = (
  name: string,
  ...rest: unknown[]
) => Promise<{ PlaceAutocompleteElement: new () => HTMLElement }>;

// The `window.google.maps` namespace as our loader observes it: a single
// mutable shape that BOTH the bootstrap (which assigns `importLibrary` + the
// inline-loader callback) and the consumer (which calls `importLibrary`) work
// against — so neither side needs a type assertion. All members are optional
// because the bootstrap creates the namespace incrementally.
interface GoogleMapsNamespace {
  importLibrary?: ImportMapsLibrary;
  /**
   * The inline loader's resolve callback, referenced by the `callback=` URL
   * param as `google.maps.__ib__`. Google invokes it once Maps JS has loaded.
   */
  __ib__?: () => void;
}

declare global {
  interface Window {
    google?: { maps?: GoogleMapsNamespace };
  }
}

export function isPlacesConfigured(): boolean {
  return Boolean(KEY);
}

// Installs Google's bootstrap shim: defines `google.maps.importLibrary`
// SYNCHRONOUSLY, then lazy-loads the real Maps JS (and the requested library)
// on first call. A plain <script> tag is unreliable — its `load` event can fire
// before `importLibrary` is registered, throwing "importLibrary is not a
// function". This is a typed reimplementation of Google's official inline loader.
function bootstrapMaps(apiKey: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // Works directly against the typed (all-optional) GoogleMapsNamespace — no
  // cast: the namespace is built up incrementally from an empty object.
  const google = (window.google ??= {});
  const maps = (google.maps ??= {});
  if (typeof maps.importLibrary === 'function') return;

  const libraries = new Set<string>();
  let loadPromise: Promise<void> | null = null;
  const load = (): Promise<void> =>
    (loadPromise ??= new Promise<void>((resolve, reject) => {
      const params = new URLSearchParams({ key: apiKey, v: 'weekly' });
      params.set('libraries', [...libraries].join(','));
      params.set('callback', 'google.maps.__ib__');
      maps.__ib__ = resolve;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
      script.async = true;
      script.dataset.googleMaps = 'true';
      script.onerror = () => reject(new Error('google-places: Maps JS load failed'));
      document.head.append(script);
    }));

  maps.importLibrary = (name: string, ...rest: unknown[]) => {
    libraries.add(name);
    // After load() the real Maps JS has replaced this shim's importLibrary, so
    // `maps.importLibrary` here is Google's own (non-null by then).
    return load().then(() => maps.importLibrary!(name, ...rest));
  };
}

// Resolves the new PlaceAutocompleteElement constructor, or null when no key is
// configured. A load failure (network / bad key) rejects.
export async function loadPlaceAutocompleteElement(): Promise<
  (new () => HTMLElement) | null
> {
  if (!KEY) return null;
  bootstrapMaps(KEY);
  const maps = window.google!.maps!;
  const places = await maps.importLibrary!('places');
  return places.PlaceAutocompleteElement;
}

export interface ParsedAddress {
  country: string;
  line1: string;
  city: string;
  state: string;
  postalCode: string;
}

// A billing address must resolve to a specific building — i.e. carry a
// `street_number`. A street-only or locality-level pick (no number) is rejected
// so we never bill an address that can't receive mail.
export function hasStreetNumber(
  components: { types: string[] }[],
): boolean {
  return components.some((c) => c.types.includes('street_number'));
}

// Maps Google address components → our flat Stripe address shape. `state` and
// `country` use shortText (ISO/abbreviated), the rest use longText.
export function parseAddressComponents(
  components: AddressComponent[],
): ParsedAddress {
  const pick = (type: string) => components.find((c) => c.types.includes(type));
  const streetNumber = pick('street_number')?.longText ?? '';
  const route = pick('route')?.longText ?? '';
  return {
    line1: [streetNumber, route].filter(Boolean).join(' '),
    city:
      pick('locality')?.longText ??
      pick('postal_town')?.longText ??
      pick('sublocality')?.longText ??
      '',
    state: pick('administrative_area_level_1')?.shortText ?? '',
    postalCode: pick('postal_code')?.longText ?? '',
    country: pick('country')?.shortText ?? '',
  };
}
